// The browser half of the library's logging: WebLog writes .NET log entries to the browser console
// (DwsWebConsoleLogger), and the JavaScript log handler forwards console output and uncaught errors to .NET
// (DwsJavaScriptLoggerExtention). Core: registered at start-up so the first errors are not lost.

import { publish } from "../shared/publish";
import { isDwsDisconnectedError } from "../shared/disconnected";

function WebLog(logEntries: string[]): void {
    const logPattern = /^\[(\w+)\] \[([^\]]+)\] (.+)$/;

    logEntries.forEach(logEntry => {
        const match = logPattern.exec(logEntry);

        if (match) {
            const logLevel = match[1];
            const logScope = match[2];
            const logMessage = match[3];

            switch (logLevel) {
                case 'Debug':
                    console.debug(`[${logScope}] ${logMessage}`);
                    break;
                case 'Information':
                    console.info(`[${logScope}] ${logMessage}`);
                    break;
                case 'Warning':
                    console.warn(`[${logScope}] ${logMessage}`);
                    break;
                case 'Error':
                    console.error(`[${logScope}] ${logMessage}`);
                    break;
                case 'Critical':
                    console.error(`[${logScope}] ${logMessage}`);
                    break;
                default:
                    console.log(`[${logScope}] ${logMessage}`);
                    break;
            }
        } else {
            console.log(logEntry);
        }
    });
}

let originalConsoleMethods = {};

// Guards for forwarding JS log messages to .NET. A failed invokeMethodAsync (e.g. while the
// Blazor circuit is disconnected) must never surface as an unhandled rejection or re-enter the
// forwarder, otherwise unhandledrejection -> forward -> reject -> ... loops forever.
let dwsIsForwardingLog = false;
let dwsLogForwardingSuspendedUntil = 0;
const DWS_LOG_RETRY_DELAY_MS = 5000;

// Pause forwarding for a while; the next successful call resumes it
function suspendDwsLogForwarding() {
    dwsLogForwardingSuspendedUntil = Date.now() + DWS_LOG_RETRY_DELAY_MS;
}

// A method of an object, not a plain function: the rejection handler recognises the forwarder's own failed sends by
// the name "forwardDwsLog" in their stack, and the minifier renames functions but keeps property names.
const dwsLogForwarder = {
    forwardDwsLog(dotNetObject: DotNetObject, payload: object): void {
        if (dwsIsForwardingLog || Date.now() < dwsLogForwardingSuspendedUntil) {
            return;
        }

        dwsIsForwardingLog = true;
        try {
            // Note: while disconnected, Blazor also rejects an internal send promise it never handles.
            // That one surfaces as unhandledrejection and is silenced in the rejection handler.
            const result = dotNetObject.invokeMethodAsync("LogJavaScriptMessage", payload);
            if (result && typeof result.then === 'function') {
                result.then(() => { dwsLogForwardingSuspendedUntil = 0; }, suspendDwsLogForwarding);
            }
        } catch (e) {
            suspendDwsLogForwarding();
        } finally {
            dwsIsForwardingLog = false;
        }
    }
};

function forwardDwsLog(dotNetObject: DotNetObject, payload: object): void {
    dwsLogForwarder.forwardDwsLog(dotNetObject, payload);
}

function serializeForLogging(arg) {
    try {
        if (arg instanceof Error) {
            return JSON.stringify({ name: arg.name, message: arg.message, stack: arg.stack });
        }
        if (typeof arg === 'object') {
            // Try safe stringify to handle circular refs
            try {
                return JSON.stringify(arg);
            } catch (e) {
                // Fallback: enumerate enumerable props
                const copy = {};
                for (const k in arg) {
                    try { copy[k] = arg[k]; } catch (__) { }
                }
                try { return JSON.stringify(copy); } catch (__) { return String(arg); }
            }
        }
        return String(arg);
    } catch (e) {
        return String(arg);
    }
}

function dwsTruncateLogText(text, maxLength) {
    if (typeof text !== 'string' || !maxLength || maxLength <= 0 || text.length <= maxLength) {
        return text;
    }
    return text.substring(0, maxLength) + '…';
}

// A "url:line:column" position inside one stack frame, or null.
function dwsParseStackFrame(frame) {
    if (typeof frame !== 'string') {
        return null;
    }
    const match = /((?:https?|file|blob):[^\s()]+?):(\d+):(\d+)\)?\s*$/.exec(frame);
    if (!match) {
        return null;
    }
    return { filename: match[1], lineno: parseInt(match[2], 10), colno: parseInt(match[3], 10) };
}

// Where the code that called console.x lives: the frame after this function and the console wrapper.
function dwsLogCallerLocation() {
    try {
        const stack = new Error().stack;
        if (typeof stack !== 'string') {
            return null;
        }
        const frames = stack.split(/\r?\n/).filter(line => /:\d+:\d+\)?\s*$/.test(line));
        return dwsParseStackFrame(frames[2]);
    } catch (e) {
        return null;
    }
}

// options: { captureErrors: bool, forwardConsole: 'off' | 'warnError' | 'all', minLevel: console.log = 1, info = 2,
// warn = 3, error = 4, maxMessageLength: number (0 = unlimited) }. Uncaught errors and unhandled rejections are
// captured whether or not console output is forwarded.
interface DwsJavascriptLogOptions {
    captureErrors?: boolean;
    forwardConsole?: "off" | "warnError" | "all";
    minLevel?: number;
    maxMessageLength?: number;
}

let dwsErrorHandler: ((event: ErrorEvent) => void) | null = null;
let dwsRejectionHandler: ((event: PromiseRejectionEvent) => void) | null = null;

function registerDwsJavascriptLogHandler(dotNetObject: DotNetObject, options?: DwsJavascriptLogOptions): void {
    options = options || {};
    const captureErrors = options.captureErrors !== false;
    const forwardConsole = options.forwardConsole || 'off';
    const minLevel = typeof options.minLevel === 'number' ? options.minLevel : 0;
    const maxMessageLength = typeof options.maxMessageLength === 'number' ? options.maxMessageLength : 0;
    const consoleRank = { log: 1, info: 2, warn: 3, error: 4 };

    // Capture Uncaught Errors
    const errorHandler = (event: ErrorEvent) => {
        // Build richer error payload
        let errorPayload = null;
        if (event.error) {
            const err = event.error;
            errorPayload = {
                name: err.name,
                message: dwsTruncateLogText(err.message, maxMessageLength),
                stack: dwsTruncateLogText(err.stack, maxMessageLength * 4)
            };
        }

        forwardDwsLog(dotNetObject, {
            kind: "error",
            level: "Error",
            message: dwsTruncateLogText(event.message || (errorPayload && errorPayload.message) || '', maxMessageLength),
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
            error: errorPayload,
            userAgent: navigator.userAgent // Add userAgent
        });
    };
    if (captureErrors) {
        window.addEventListener("error", errorHandler);
    }

    // Capture Unhandled Promise Rejections
    const rejectionHandler = (event: PromiseRejectionEvent) => {
        // Normalize reason which may be an Error, string or other
        let reason = event.reason;
        let message = "Unhandled Promise Rejection";
        let errorPayload = null;

        if (reason) {
            if (reason instanceof Error) {
                message = reason.message || message;
                errorPayload = { name: reason.name, message: reason.message, stack: reason.stack };
            } else if (typeof reason === 'string') {
                message = reason;
            } else {
                try {
                    message = JSON.stringify(reason);
                    errorPayload = reason;
                } catch (e) {
                    message = String(reason);
                }
            }
        }

        // Never report failed interop calls caused by a disconnected circuit; stop forwarding
        // until the connection is back, and silence the rejections our own forwarding caused
        if (isDwsDisconnectedError(message)) {
            suspendDwsLogForwarding();
            if (reason && typeof reason.stack === 'string' && reason.stack.indexOf('forwardDwsLog') !== -1) {
                event.preventDefault();
            }
            return;
        }

        // A rejection event has no position of its own: take the first frame of the reason's stack.
        let location = null;
        if (reason && typeof reason.stack === 'string') {
            const frames = reason.stack.split(/\r?\n/).filter(line => /:\d+:\d+\)?\s*$/.test(line));
            location = dwsParseStackFrame(frames[0]);
        }

        if (errorPayload && typeof errorPayload === 'object') {
            errorPayload = {
                name: errorPayload.name,
                message: dwsTruncateLogText(typeof errorPayload.message === 'string' ? errorPayload.message : message, maxMessageLength),
                stack: dwsTruncateLogText(errorPayload.stack, maxMessageLength * 4)
            };
        }

        forwardDwsLog(dotNetObject, {
            kind: "rejection",
            level: "Error",
            message: dwsTruncateLogText(message, maxMessageLength),
            filename: location ? location.filename : undefined,
            lineno: location ? location.lineno : undefined,
            colno: location ? location.colno : undefined,
            error: errorPayload,
            userAgent: navigator.userAgent // Add userAgent
        });
    };
    if (captureErrors) {
        window.addEventListener("unhandledrejection", rejectionHandler);
    }

    // Override Console Methods - only the levels the application asked for, so nothing else leaves the browser
    const consoleLevels = forwardConsole === 'all' ? ["log", "warn", "error", "info"] : (forwardConsole === 'warnError' ? ["warn", "error"] : []);
    consoleLevels.filter(level => consoleRank[level] >= minLevel).forEach(level => {
        if (!originalConsoleMethods[level]) {
            originalConsoleMethods[level] = console[level]; // Store original method
        }
        console[level] = (...args) => {
            originalConsoleMethods[level].apply(console, args); // Preserve default behavior
            if (dwsIsForwardingLog) {
                return;
            }
            try {
                const payloadMessage = dwsTruncateLogText(args.map(a => serializeForLogging(a)).join(" "), maxMessageLength);
                const location = dwsLogCallerLocation();
                forwardDwsLog(dotNetObject, {
                    kind: "console",
                    level: level.charAt(0).toUpperCase() + level.slice(1),
                    message: payloadMessage,
                    filename: location ? location.filename : undefined,
                    lineno: location ? location.lineno : undefined,
                    colno: location ? location.colno : undefined,
                    userAgent: navigator.userAgent // Add userAgent
                });
            } catch (e) {
                // Don't let logging break app
            }
        };
    });

    // Store handlers for deregistration
    dwsErrorHandler = errorHandler;
    dwsRejectionHandler = rejectionHandler;
}

function unregisterDwsJavascriptLogHandler(): void {
    // Remove Uncaught Errors
    if (dwsErrorHandler) {
        window.removeEventListener("error", dwsErrorHandler);
        dwsErrorHandler = null;
    }

    // Remove Unhandled Promise Rejections
    if (dwsRejectionHandler) {
        window.removeEventListener("unhandledrejection", dwsRejectionHandler);
        dwsRejectionHandler = null;
    }

    // Restore Original Console Methods
    ["log", "warn", "error", "info"].forEach(level => {
        if (originalConsoleMethods[level]) {
            console[level] = originalConsoleMethods[level];
            delete originalConsoleMethods[level]; // Clean up stored reference
        }
    });
}

publish("WebLog", WebLog);
publish("registerDwsJavascriptLogHandler", registerDwsJavascriptLogHandler);
publish("unregisterDwsJavascriptLogHandler", unregisterDwsJavascriptLogHandler);
